import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import "./Profile.css";
import { useLanguage } from "../i18n/LanguageContext";

export default function Profile() {
  const { t } = useLanguage();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [fotoPerfil, setFotoPerfil] = useState("");

  const [balance, setBalance] = useState(null);
  const [primerPago, setPrimerPago] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Escucha cambios de sesión (por si recarga la página)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user || null);
    });
    return () => unsub();
  }, []);

  // Cargar datos de Firestore
  useEffect(() => {
    const loadData = async () => {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        // Documento principal de usuario
        const userRef = doc(db, "usuarios", firebaseUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setNombre(data.nombre || "");
          setCorreo(data.correo || firebaseUser.email || "");
          setFotoPerfil(data.fotoPerfil || "");
        } else {
          // Si no existe, al menos usamos datos básicos del auth
          setNombre(firebaseUser.displayName || "");
          setCorreo(firebaseUser.email || "");
        }

        // Wallet: subcolección "wallet" doc "main"
        const walletRef = doc(db, "usuarios", firebaseUser.uid, "wallet", "main");
        const walletSnap = await getDoc(walletRef);

        if (walletSnap.exists()) {
          const w = walletSnap.data();
          setBalance(typeof w.balance === "number" ? w.balance : null);
          setPrimerPago(w.primerPago ?? null);
          setUpdatedAt(w.updatedAt ?? null);
        } else {
          setBalance(null);
          setPrimerPago(null);
          setUpdatedAt(null);
        }
      } catch (err) {
        console.error("Error cargando perfil:", err);
        // Mantengo tu texto original, pero si quieres lo hacemos i18n también
        setError("Ocurrió un error al cargar tu perfil.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [firebaseUser]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!firebaseUser) return;

    try {
      setSaving(true);
      setError("");

      const userRef = doc(db, "usuarios", firebaseUser.uid);
      await updateDoc(userRef, {
        nombre: nombre.trim(),
      });
    } catch (err) {
      console.error("Error guardando nombre:", err);
      // Mantengo tu texto original, pero si quieres lo hacemos i18n también
      setError("No se pudo guardar tu nombre. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const formatUpdatedAt = (value) => {
    if (!value) return "Sin registro";

    try {
      // Si es Timestamp de Firestore
      const date =
        typeof value.toDate === "function"
          ? value.toDate()
          : value instanceof Date
          ? value
          : new Date(value);

      return date.toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return "Fecha no disponible";
    }
  };

  if (!firebaseUser && !loading) {
    return (
      <div className="profile-page">
        <div className="profile-card">
          <p>{t("auth.notLoggedIn")}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="spinner" />
          <p>{t("profile.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-grid">
        <section className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-wrapper">
              {fotoPerfil ? (
                <img
                  src={fotoPerfil}
                  alt={nombre || correo}
                  className="profile-avatar"
                />
              ) : (
                <div className="profile-avatar fallback">
                  <span>{(nombre || correo || "?").charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>

            <div className="profile-header-text">
              <h1 className="profile-name-title">
                {nombre || t("profile.nameFallback")}
              </h1>

              <p className="profile-email">{correo}</p>

              <p className="profile-subtitle">{t("profile.subtitle")}</p>
            </div>
          </div>

          <form className="profile-form" onSubmit={handleSave}>
            <label className="profile-label">
              {t("profile.usernameLabel")}
              <input
                type="text"
                className="profile-input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                maxLength={60}
                placeholder={t("profile.usernamePlaceholder")}
              />
            </label>

            <label className="profile-label">
              {t("profile.emailLabel")}
              <input
                type="email"
                className="profile-input"
                value={correo}
                disabled
              />
              <span className="profile-help-text">
                {t("profile.emailNotEditable")}
              </span>
            </label>

            {error && <p className="profile-error">{error}</p>}

            <button
              type="submit"
              className="profile-save-button"
              disabled={saving}
            >
              {saving ? t("profile.saving") : t("profile.saveChanges")}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
